import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ListingType } from '@app/listings/featured-properties/listing-type';
import { firstValueFrom, of } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import { environment } from '@env/environment';
import { PropertyRecord } from '@app/models/property.model';

interface BulkListingsPage {
  items: PropertyRecord[];
  total?: number;
  limit?: number | null;
  offset?: number;
}

type BulkListingsBucket = PropertyRecord[] | BulkListingsPage;

interface BulkListingsPayload {
  residential?: BulkListingsBucket;
  commercial?: BulkListingsBucket;
  industrial?: BulkListingsBucket;
  land?: BulkListingsBucket;
}

interface ListingsCachePayload {
  savedAt: number;
  featuredResidentialProperties: PropertyRecord[];
  featuredCommercialProperties: PropertyRecord[];
  featuredIndustrialProperties: PropertyRecord[];
  featuredLandProperties: PropertyRecord[];
  allResidentialProperties: PropertyRecord[];
  allCommercialProperties: PropertyRecord[];
  allIndustrialProperties: PropertyRecord[];
  allLandProperties: PropertyRecord[];
}

interface RentListingsCachePayload {
  savedAt: number;
  featuredRentProperties: PropertyRecord[];
}

type ListingsUpdateListener = () => void;
type BulkEndpoint = 'sale' | 'rent';

export type { ListingType };

@Injectable({
  providedIn: 'root'
})
export class LoadPropertiesService {
  private readonly http = inject(HttpClient);

  static featuredResidentialProperties: PropertyRecord[] = [];
  static featuredCommercialProperties: PropertyRecord[] = [];
  static featuredIndustrialProperties: PropertyRecord[] = [];
  static featuredLandProperties: PropertyRecord[] = [];
  static allResidentialProperties: PropertyRecord[] = [];
  static allCommercialProperties: PropertyRecord[] = [];
  static allIndustrialProperties: PropertyRecord[] = [];
  static allLandProperties: PropertyRecord[] = [];
  static featuredRentProperties: PropertyRecord[] = [];
  static propertiesLoaded = false;
  static rentPropertiesLoaded = false;
  static lastUpdatedTypes: ListingType[] = [];

  private static readonly CACHE_KEY = 'gabali:listings:v16';
  private static readonly RENT_CACHE_KEY = 'gabali:rent-listings:v1';
  private static readonly CACHE_TTL_MS = 10 * 60 * 1000;
  private static readonly REQUEST_TIMEOUT_MS = 60_000;
  private static readonly BULK_FIELDS = 'card';
  private static readonly BULK_PAGE_SIZE = 2000;
  private static readonly PRIMARY_TYPES: ListingType[] = ['residential'];
  private static readonly SECONDARY_TYPES: ListingType[] = ['commercial', 'industrial', 'land'];
  private static readonly ALL_TYPES: ListingType[] = [
    'residential',
    'commercial',
    'industrial',
    'land',
  ];

  private static loadPromise: Promise<void> | null = null;
  private static rentLoadPromise: Promise<void> | null = null;
  private static isRefreshing = false;
  private static isRentRefreshing = false;

  constructor() {}

  /** Load for-sale listings for the featured properties page. */
  loadForFeaturedPage(onUpdate?: ListingsUpdateListener): Promise<void> {
    return LoadPropertiesService.loadProperties(this.http, onUpdate);
  }

  /** Load featured rental listings for the for-rent page. */
  loadForRentPage(onUpdate?: ListingsUpdateListener): Promise<void> {
    return LoadPropertiesService.loadRentProperties(this.http, onUpdate);
  }

  /** Warm for-sale listings while the user is still on other pages. */
  static preload(http: HttpClient, onUpdate?: ListingsUpdateListener): void {
    void this.loadProperties(http, onUpdate);
  }

  static loadProperties(http: HttpClient, onUpdate?: ListingsUpdateListener): Promise<void> {
    if (this.propertiesLoaded && !this.isRefreshing) {
      this.lastUpdatedTypes = [...this.ALL_TYPES];
      onUpdate?.();
      return Promise.resolve();
    }

    if (this.loadPromise) {
      if (onUpdate && this.propertiesLoaded) {
        this.lastUpdatedTypes = [...this.ALL_TYPES];
        onUpdate();
      }

      return this.loadPromise.then(() => {
        this.lastUpdatedTypes = [...this.ALL_TYPES];
        onUpdate?.();
      });
    }

    this.loadPromise = this.executeLoad(http, onUpdate);
    return this.loadPromise;
  }

  static loadRentProperties(http: HttpClient, onUpdate?: ListingsUpdateListener): Promise<void> {
    if (this.rentPropertiesLoaded && !this.isRentRefreshing) {
      onUpdate?.();
      return Promise.resolve();
    }

    if (this.rentLoadPromise) {
      if (onUpdate && this.rentPropertiesLoaded) {
        onUpdate();
      }

      return this.rentLoadPromise.then(() => onUpdate?.());
    }

    this.rentLoadPromise = this.executeRentLoad(http, onUpdate);
    return this.rentLoadPromise;
  }

  static resetForTests(): void {
    this.featuredResidentialProperties = [];
    this.featuredCommercialProperties = [];
    this.featuredIndustrialProperties = [];
    this.featuredLandProperties = [];
    this.allResidentialProperties = [];
    this.allCommercialProperties = [];
    this.allIndustrialProperties = [];
    this.allLandProperties = [];
    this.featuredRentProperties = [];
    this.propertiesLoaded = false;
    this.rentPropertiesLoaded = false;
    this.lastUpdatedTypes = [];
    this.loadPromise = null;
    this.rentLoadPromise = null;
    this.isRefreshing = false;
    this.isRentRefreshing = false;

    try {
      sessionStorage.removeItem(this.CACHE_KEY);
      sessionStorage.removeItem(this.RENT_CACHE_KEY);
    } catch {
      // Ignore storage errors in tests.
    }
  }

  private static async executeLoad(
    http: HttpClient,
    onUpdate?: ListingsUpdateListener,
  ): Promise<void> {
    const hydratedFromCache = this.tryHydrateCache();

    if (hydratedFromCache) {
      this.lastUpdatedTypes = [...this.ALL_TYPES];
      this.propertiesLoaded = true;
      onUpdate?.();
      this.isRefreshing = true;

      try {
        await this.fetchFromNetwork(http, onUpdate, 'sale');
      } catch {
        // Keep cached listings when a background refresh fails.
      } finally {
        this.isRefreshing = false;
      }

      return;
    }

    await this.fetchFromNetwork(http, onUpdate, 'sale');
  }

  private static async executeRentLoad(
    http: HttpClient,
    onUpdate?: ListingsUpdateListener,
  ): Promise<void> {
    const hydratedFromCache = this.tryHydrateRentCache();

    if (hydratedFromCache) {
      this.rentPropertiesLoaded = true;
      onUpdate?.();
      this.isRentRefreshing = true;

      try {
        await this.fetchRentFromNetwork(http, onUpdate);
      } catch {
        // Keep cached rentals when a background refresh fails.
      } finally {
        this.isRentRefreshing = false;
      }

      return;
    }

    await this.fetchRentFromNetwork(http, onUpdate);
  }

  private static async fetchFromNetwork(
    http: HttpClient,
    onUpdate: ListingsUpdateListener | undefined,
    endpoint: BulkEndpoint,
  ): Promise<void> {
    const primaryPayload = await this.loadBulk(http, this.PRIMARY_TYPES, endpoint);
    this.lastUpdatedTypes = [...this.PRIMARY_TYPES];
    this.applyBulkPayload(primaryPayload);
    this.propertiesLoaded = true;
    onUpdate?.();

    const secondaryPayload = await this.loadBulk(http, this.SECONDARY_TYPES, endpoint);
    this.lastUpdatedTypes = [...this.SECONDARY_TYPES];
    this.applyBulkPayload(secondaryPayload);
    onUpdate?.();
    this.lastUpdatedTypes = [...this.ALL_TYPES];
    this.scheduleCacheSave();
  }

  private static async fetchRentFromNetwork(
    http: HttpClient,
    onUpdate?: ListingsUpdateListener,
  ): Promise<void> {
    const primaryPayload = await this.loadBulk(http, this.PRIMARY_TYPES, 'rent');
    this.applyRentBulkPayload(primaryPayload);
    this.rentPropertiesLoaded = true;
    onUpdate?.();

    const secondaryPayload = await this.loadBulk(http, this.SECONDARY_TYPES, 'rent');
    this.applyRentBulkPayload(secondaryPayload);
    onUpdate?.();
    this.scheduleRentCacheSave();
  }

  private static async loadBulk(
    http: HttpClient,
    types: ListingType[],
    endpoint: BulkEndpoint,
  ): Promise<BulkListingsPayload> {
    const payload: BulkListingsPayload = {};

    await Promise.all(
      types.map(async (type) => {
        const items = await this.fetchAllPagesForType(http, type, endpoint);
        if (items.length > 0) {
          payload[type] = items;
        }
      }),
    );

    return payload;
  }

  private static bulkPath(endpoint: BulkEndpoint): string {
    return endpoint === 'rent' ? 'listings/rent/bulk' : 'listings/bulk';
  }

  private static async fetchAllPagesForType(
    http: HttpClient,
    type: ListingType,
    endpoint: BulkEndpoint,
  ): Promise<PropertyRecord[]> {
    const api = environment.API_URL;
    const items: PropertyRecord[] = [];
    let offset = 0;
    let total: number | undefined;

    while (total === undefined || offset < total) {
      const groupBy =
        endpoint === 'rent' ? '&groupBy=building' : '';
      const url =
        `${api}${this.bulkPath(endpoint)}?types=${type}&fields=${this.BULK_FIELDS}&meta=1&limit=${this.BULK_PAGE_SIZE}&offset=${offset}${groupBy}`;

      try {
        const response = await firstValueFrom(
          http.get<BulkListingsPayload>(url).pipe(
            timeout(this.REQUEST_TIMEOUT_MS),
            catchError(() => of({} as BulkListingsPayload)),
          ),
        );

        const bucket = response?.[type];
        if (!bucket) {
          break;
        }

        if (Array.isArray(bucket)) {
          return bucket;
        }

        const page = bucket as BulkListingsPage;
        const pageItems = page.items ?? [];
        items.push(...pageItems);
        total = page.total ?? items.length;
        offset += pageItems.length;

        if (pageItems.length === 0) {
          break;
        }
      } catch {
        break;
      }
    }

    return items;
  }

  private static applyBulkPayload(payload: BulkListingsPayload): void {
    this.applyTypeBucket('residential', payload.residential);
    this.applyTypeBucket('commercial', payload.commercial);
    this.applyTypeBucket('industrial', payload.industrial);
    this.applyTypeBucket('land', payload.land);
  }

  private static applyRentBulkPayload(payload: BulkListingsPayload): void {
    const listings = [
      ...this.normalizeBulkBucket(payload.residential) ?? [],
      ...this.normalizeBulkBucket(payload.commercial) ?? [],
      ...this.normalizeBulkBucket(payload.industrial) ?? [],
      ...this.normalizeBulkBucket(payload.land) ?? [],
    ];

    if (listings.length === 0) {
      return;
    }

    const merged = [...this.featuredRentProperties];
    const seen = new Set(merged.map((listing) => String(listing.ListingID ?? '')));
    for (const listing of listings) {
      const listingId = String(listing.ListingID ?? '');
      if (!listingId || seen.has(listingId)) {
        continue;
      }
      seen.add(listingId);
      merged.push(listing);
    }
    this.featuredRentProperties = merged;
  }

  private static normalizeBulkBucket(bucket: BulkListingsBucket | undefined): PropertyRecord[] | undefined {
    if (!bucket) {
      return undefined;
    }

    if (Array.isArray(bucket)) {
      return bucket;
    }

    return Array.isArray(bucket.items) ? bucket.items : undefined;
  }

  private static applyTypeBucket(type: ListingType, bucket: BulkListingsBucket | undefined): void {
    const listings = this.normalizeBulkBucket(bucket);
    if (!listings) {
      return;
    }

    const featured = listings.filter((listing) => this.isFeaturedListing(listing));
    const all = listings;

    switch (type) {
      case 'residential':
        this.allResidentialProperties = all;
        this.featuredResidentialProperties = featured;
        break;
      case 'commercial':
        this.allCommercialProperties = all;
        this.featuredCommercialProperties = featured;
        break;
      case 'industrial':
        this.allIndustrialProperties = all;
        this.featuredIndustrialProperties = featured;
        break;
      case 'land':
        this.allLandProperties = all;
        this.featuredLandProperties = featured;
        break;
    }
  }

  private static isFeaturedListing(listing: object): boolean {
    return (listing as Record<string, unknown>)['IsFeatured'] === true;
  }

  private static tryHydrateCache(): boolean {
    try {
      const raw = sessionStorage.getItem(this.CACHE_KEY);
      if (!raw) {
        return false;
      }

      const payload = JSON.parse(raw) as ListingsCachePayload;
      if (!payload?.savedAt || Date.now() - payload.savedAt > this.CACHE_TTL_MS) {
        sessionStorage.removeItem(this.CACHE_KEY);
        return false;
      }

      this.featuredResidentialProperties = payload.featuredResidentialProperties ?? [];
      this.featuredCommercialProperties = payload.featuredCommercialProperties ?? [];
      this.featuredIndustrialProperties = payload.featuredIndustrialProperties ?? [];
      this.featuredLandProperties = payload.featuredLandProperties ?? [];
      this.allResidentialProperties = payload.allResidentialProperties ?? [];
      this.allCommercialProperties = payload.allCommercialProperties ?? [];
      this.allIndustrialProperties = payload.allIndustrialProperties ?? [];
      this.allLandProperties = payload.allLandProperties ?? [];
      return this.getTotalCount() > 0;
    } catch {
      return false;
    }
  }

  private static tryHydrateRentCache(): boolean {
    try {
      const raw = sessionStorage.getItem(this.RENT_CACHE_KEY);
      if (!raw) {
        return false;
      }

      const payload = JSON.parse(raw) as RentListingsCachePayload;
      if (!payload?.savedAt || Date.now() - payload.savedAt > this.CACHE_TTL_MS) {
        sessionStorage.removeItem(this.RENT_CACHE_KEY);
        return false;
      }

      this.featuredRentProperties = payload.featuredRentProperties ?? [];
      return this.featuredRentProperties.length > 0;
    } catch {
      return false;
    }
  }

  private static scheduleCacheSave(): void {
    const payload: ListingsCachePayload = {
      savedAt: Date.now(),
      featuredResidentialProperties: this.featuredResidentialProperties,
      featuredCommercialProperties: this.featuredCommercialProperties,
      featuredIndustrialProperties: this.featuredIndustrialProperties,
      featuredLandProperties: this.featuredLandProperties,
      allResidentialProperties: this.allResidentialProperties,
      allCommercialProperties: this.allCommercialProperties,
      allIndustrialProperties: this.allIndustrialProperties,
      allLandProperties: this.allLandProperties,
    };

    this.scheduleSessionStorageSave(this.CACHE_KEY, payload);
  }

  private static scheduleRentCacheSave(): void {
    const payload: RentListingsCachePayload = {
      savedAt: Date.now(),
      featuredRentProperties: this.featuredRentProperties,
    };

    this.scheduleSessionStorageSave(this.RENT_CACHE_KEY, payload);
  }

  private static scheduleSessionStorageSave(key: string, payload: unknown): void {
    const save = () => {
      try {
        sessionStorage.setItem(key, JSON.stringify(payload));
      } catch {
        // Payload can exceed sessionStorage quota on some browsers.
      }
    };

    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(save, { timeout: 2000 });
      return;
    }

    setTimeout(save, 0);
  }

  private static getTotalCount(): number {
    return this.featuredResidentialProperties.length
      + this.featuredCommercialProperties.length
      + this.featuredIndustrialProperties.length
      + this.featuredLandProperties.length
      + this.allResidentialProperties.length
      + this.allCommercialProperties.length
      + this.allIndustrialProperties.length
      + this.allLandProperties.length;
  }

  static getAllListings(): PropertyRecord[] {
    return [
      ...this.allResidentialProperties,
      ...this.allCommercialProperties,
      ...this.allIndustrialProperties,
      ...this.allLandProperties,
    ];
  }
}
